import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, ChevronUp, ChevronDown, Check, ArrowUp, ArrowDown, Shield, Cog, Calendar, KeyRound, AlertTriangle, ChevronRight, HelpCircle } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { PUZZLE_2_SAFE_CRACKER } from '@/config/missionContent';

const CORRECT_CODE = CONFIG.safeCode;

/* ─── Memoized background particles ─── */
function VaultBackground() {
  const particles = useMemo(() =>
    Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: `${5 + (i * 9.3) % 90}%`,
      top: `${3 + (i * 11.7) % 94}%`,
      size: 1 + (i % 3) * 0.5,
      duration: 8 + (i % 4) * 3,
      delay: i * 0.7,
      opacity: 0.15 + (i % 3) * 0.08,
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {/* Steel gradient base */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#050508] to-black" />

      {/* Steel-blue grid */}
      <div className="absolute inset-0 opacity-[0.02]"
        style={{ backgroundImage: 'linear-gradient(rgba(100,140,200,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(100,140,200,0.4) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />

      {/* Floating dust */}
      {particles.map(p => (
        <motion.div key={p.id}
          className="absolute rounded-full"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size, background: `rgba(100,160,220,${p.opacity})` }}
          animate={{ y: [0, -15, 0], x: [0, 5, -3, 0], opacity: [p.opacity, p.opacity * 1.5, p.opacity] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
        />
      ))}

      {/* Steel-blue vignette */}
      <div className="absolute inset-0 animate-pulse" style={{ animationDuration: '6s', background: 'radial-gradient(ellipse at center, transparent 40%, rgba(20,40,80,0.15) 100%)' }} />

      {/* CRT scanlines */}
      <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(100,160,220,0.15) 2px, rgba(100,160,220,0.15) 4px)', backgroundSize: '100% 4px' }} />
    </div>
  );
}

/* ─── Enhanced Vault Door SVG ─── */
function VaultDoor({ solved }) {
  const boltPositions = useMemo(() =>
    [0, 45, 90, 135, 180, 225, 270, 315].map(angle => {
      const rad = (angle * Math.PI) / 180;
      return { angle, x: 200 + Math.cos(rad) * 175, y: 200 + Math.sin(rad) * 175 };
    }), []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.08]">
      <svg viewBox="0 0 400 400" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px]">
        {/* Outer ring */}
        <circle cx="200" cy="200" r="185" fill="none" stroke="rgba(0,255,65,0.2)" strokeWidth="0.3" />
        <circle cx="200" cy="200" r="180" fill="none" stroke="rgba(0,255,65,0.35)" strokeWidth="0.5" strokeDasharray="4 8"
          className={solved ? '' : 'animate-spin-slow'} style={{ animationDuration: '30s', transformOrigin: '200px 200px', filter: 'drop-shadow(0 0 2px rgba(0,255,65,0.3))' }} />
        {/* Middle ring */}
        <circle cx="200" cy="200" r="150" fill="none" stroke="rgba(0,255,65,0.25)" strokeWidth="0.5" strokeDasharray="2 12"
          className={solved ? '' : 'animate-spin-slow'} style={{ animationDuration: '20s', animationDirection: 'reverse', transformOrigin: '200px 200px', filter: 'drop-shadow(0 0 2px rgba(0,255,65,0.2))' }} />
        {/* Inner ring */}
        <circle cx="200" cy="200" r="120" fill="none" stroke="rgba(0,255,65,0.15)" strokeWidth="0.5" />
        <circle cx="200" cy="200" r="90" fill="none" stroke="rgba(0,255,65,0.08)" strokeWidth="0.3" strokeDasharray="1 6" />
        {/* Bolt heads */}
        {boltPositions.map(b => (
          <g key={b.angle}>
            <circle cx={b.x} cy={b.y} r="4" fill="rgba(0,255,65,0.1)" stroke="rgba(0,255,65,0.2)" strokeWidth="0.5" />
            <circle cx={b.x} cy={b.y} r="1.5" fill="rgba(0,255,65,0.25)" />
          </g>
        ))}
        {/* Center keyhole */}
        <circle cx="200" cy="200" r="15" fill="none" stroke="rgba(0,255,65,0.12)" strokeWidth="0.5" />
        <rect x="197" y="188" width="6" height="18" rx="2" fill="rgba(0,255,65,0.08)" />
      </svg>
    </div>
  );
}

/* ─── HUD Corners ─── */
function HUDCorners() {
  return (
    <>
      {[['top-0 left-0', 'border-t border-l'], ['top-0 right-0', 'border-t border-r'], ['bottom-0 left-0', 'border-b border-l'], ['bottom-0 right-0', 'border-b border-r']].map(([pos, borders]) => (
        <div key={pos} className={`absolute ${pos} w-6 h-6 ${borders} border-green-700/20 pointer-events-none`} />
      ))}
    </>
  );
}

/* ─── Enhanced Digit Wheel ─── */
function DigitWheel({ digit, index, feedback, solved, onUp, onDown }) {
  const fbState = feedback?.[index];
  const isCorrect = fbState === 'correct';
  const isWrong = fbState === 'higher' || fbState === 'lower';

  return (
    <div className="flex flex-col items-center gap-1" data-testid={`dial-col-${index}`}>
      {/* Position label */}
      <span className="text-xs font-terminal text-green-800 tracking-wider">{String(index + 1).padStart(2, '0')}</span>

      {/* Up button */}
      <motion.button
        whileHover={!solved && !isCorrect ? { scale: 1.1, y: -1 } : {}}
        whileTap={!solved && !isCorrect ? { scale: 0.9 } : {}}
        className={`w-12 h-7 rounded-t border border-b-0 flex items-center justify-center transition-all
          ${isCorrect ? 'border-green-800/10 text-green-800/10 cursor-default' : solved ? 'border-green-800/20 text-green-800/20' : 'border-green-800/30 text-green-500 hover:border-green-600/50 hover:bg-green-950/20'}`}
        onClick={() => onUp(index)}
        disabled={solved}
        data-testid={`dial-up-${index}`}
      >
        <ChevronUp size={14} />
      </motion.button>

      {/* Digit display */}
      <motion.div
        animate={isCorrect ? { borderColor: '#22c55e', boxShadow: '0 0 18px rgba(34,197,94,0.35)' } : isWrong ? { x: [-3, 3, -3, 0] } : {}}
        transition={isWrong ? { duration: 0.3 } : { duration: 0.5 }}
        className={`w-12 h-14 flex items-center justify-center font-orbitron text-2xl border-2 rounded-sm relative overflow-hidden transition-all duration-500
          ${isCorrect ? 'border-green-500 bg-green-950/40 text-green-300' : isWrong ? 'border-red-600/60 bg-red-950/20 text-red-300' : solved ? 'border-green-500 bg-green-950/40 text-green-300' : 'border-green-800/30 bg-black/60 text-green-300'}`}
      >
        {/* Top/bottom gradient overlays */}
        <div className="absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        {/* Number */}
        <motion.span key={digit} initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.15 }}>
          {digit}
        </motion.span>
        {/* Lock indicator for correct */}
        {isCorrect && (
          <motion.div initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} className="absolute -top-0.5 -right-0.5">
            <div className="w-3.5 h-3.5 rounded-full bg-green-500 flex items-center justify-center" style={{ boxShadow: '0 0 8px rgba(0,255,65,0.6)' }}>
              <Check size={8} className="text-black" strokeWidth={3} />
            </div>
          </motion.div>
        )}
        {/* Correct glow pulse overlay */}
        {isCorrect && (
          <div className="absolute inset-0 animate-pulse rounded-sm pointer-events-none" style={{ animationDuration: '2s', boxShadow: 'inset 0 0 12px rgba(34,197,94,0.15)' }} />
        )}
      </motion.div>

      {/* Down button */}
      <motion.button
        whileHover={!solved && !isCorrect ? { scale: 1.1, y: 1 } : {}}
        whileTap={!solved && !isCorrect ? { scale: 0.9 } : {}}
        className={`w-12 h-7 rounded-b border border-t-0 flex items-center justify-center transition-all
          ${isCorrect ? 'border-green-800/10 text-green-800/10 cursor-default' : solved ? 'border-green-800/20 text-green-800/20' : 'border-green-800/30 text-green-500 hover:border-green-600/50 hover:bg-green-950/20'}`}
        onClick={() => onDown(index)}
        disabled={solved}
        data-testid={`dial-down-${index}`}
      >
        <ChevronDown size={14} />
      </motion.button>

      {/* Feedback arrow */}
      <div className="h-5 flex items-center justify-center">
        {fbState === 'higher' && (
          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center">
            <ArrowUp size={14} className="text-red-400" />
          </motion.div>
        )}
        {fbState === 'lower' && (
          <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center">
            <ArrowDown size={14} className="text-red-400" />
          </motion.div>
        )}
        {isCorrect && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <Check size={14} className="text-green-400" />
          </motion.div>
        )}
      </div>
    </div>
  );
}

/* ─── Tutorial Screen ─── */
function VaultTutorial({ onStart }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
      className="relative z-10 max-w-lg mx-auto space-y-5">
      {/* Tutorial header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <HelpCircle size={14} className="text-green-500" />
          <span className="text-xs font-terminal tracking-[5px] text-green-500 uppercase">{PUZZLE_2_SAFE_CRACKER.tutorial.heading}</span>
          <HelpCircle size={14} className="text-green-500" />
        </div>
        <p className="text-xs font-terminal text-green-700 tracking-wider">{PUZZLE_2_SAFE_CRACKER.tutorial.subheading}</p>
      </div>

      {/* How it works */}
      <div className="bg-black/40 border border-green-900/30 rounded p-4 space-y-3"
        style={{ boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3)' }}>
        <div className="flex items-center gap-2 pb-2 border-b border-green-900/20">
          <Lock size={12} className="text-green-600" />
          <span className="text-xs font-terminal tracking-[3px] text-green-600 uppercase">{PUZZLE_2_SAFE_CRACKER.tutorial.howItWorks}</span>
        </div>

        <div className="space-y-2.5">
          {[
            { icon: ChevronUp, label: PUZZLE_2_SAFE_CRACKER.tutorial.steps[0], color: 'text-green-400' },
            { icon: Lock, label: PUZZLE_2_SAFE_CRACKER.tutorial.steps[1], color: 'text-yellow-400' },
            { icon: Check, label: PUZZLE_2_SAFE_CRACKER.tutorial.steps[2], color: 'text-green-400' },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.15 }}
              className="flex items-center gap-3">
              <div className="w-7 h-7 rounded border border-green-800/30 bg-green-950/30 flex items-center justify-center flex-shrink-0">
                <item.icon size={14} className={item.color} />
              </div>
              <span className="text-xs font-terminal text-gray-400 tracking-wider">{item.label}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Feedback guide */}
      <div className="bg-black/40 border border-green-900/30 rounded p-4 space-y-3"
        style={{ boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3)' }}>
        <div className="flex items-center gap-2 pb-2 border-b border-green-900/20">
          <AlertTriangle size={12} className="text-yellow-600" />
          <span className="text-xs font-terminal tracking-[3px] text-yellow-600 uppercase">{PUZZLE_2_SAFE_CRACKER.tutorial.feedbackGuide}</span>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: ArrowUp, label: PUZZLE_2_SAFE_CRACKER.tutorial.feedbackItems[0].label, desc: PUZZLE_2_SAFE_CRACKER.tutorial.feedbackItems[0].desc, color: 'text-red-400', borderColor: 'border-red-800/30' },
            { icon: ArrowDown, label: PUZZLE_2_SAFE_CRACKER.tutorial.feedbackItems[1].label, desc: PUZZLE_2_SAFE_CRACKER.tutorial.feedbackItems[1].desc, color: 'text-red-400', borderColor: 'border-red-800/30' },
            { icon: Check, label: PUZZLE_2_SAFE_CRACKER.tutorial.feedbackItems[2].label, desc: PUZZLE_2_SAFE_CRACKER.tutorial.feedbackItems[2].desc, color: 'text-green-400', borderColor: 'border-green-800/30' },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + i * 0.1 }}
              className={`text-center p-2 rounded border ${item.borderColor} bg-black/30`}>
              <item.icon size={16} className={`${item.color} mx-auto mb-1`} />
              <p className={`text-xs font-terminal tracking-wider ${item.color}`}>{item.label}</p>
              <p className="text-xs font-terminal text-gray-600 mt-0.5">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Hint */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
        className="text-center">
        <div className="inline-flex items-center gap-2 bg-yellow-950/20 border border-yellow-800/20 rounded px-3 py-1.5">
          <Calendar size={10} className="text-yellow-600" />
          <span className="text-xs font-terminal text-yellow-600/80 tracking-wider italic">{PUZZLE_2_SAFE_CRACKER.tutorial.hint}</span>
        </div>
      </motion.div>

      {/* Start button */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
        className="text-center pt-2">
        <motion.button
          whileHover={{ scale: 1.03, boxShadow: '0 0 25px rgba(34,197,94,0.2)' }}
          whileTap={{ scale: 0.97 }}
          onClick={onStart}
          className="group relative px-10 py-3 bg-gradient-to-r from-green-950/50 via-green-900/30 to-green-950/50 border-2 border-green-600/60 text-green-400 font-terminal text-base tracking-[5px] uppercase transition-all hover:border-green-500/80 overflow-hidden"
        >
          {/* Shimmer */}
          <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/8 to-transparent pointer-events-none"
            animate={{ x: ['-200%', '200%'] }} transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 0.5 }} />
          {/* Pulse ring */}
          <motion.div className="absolute inset-0 border border-green-500/20 rounded-sm pointer-events-none"
            animate={{ opacity: [0, 0.5, 0] }} transition={{ duration: 2, repeat: Infinity }} />
          <span className="relative z-10 flex items-center justify-center gap-2">
            <Lock size={14} /> {PUZZLE_2_SAFE_CRACKER.tutorial.startButton} <ChevronRight size={14} />
          </span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

/* ─── Main Component ─── */
export default function Puzzle2SafeCracker({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [puzzleActive, setPuzzleActive] = useState(false);
  const [digits, setDigits] = useState([9, 9, 9, 9, 9, 9, 9, 9]);
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [solved, setSolved] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [successPhase, setSuccessPhase] = useState(0);
  const [showProceed, setShowProceed] = useState(false);
  const panelRef = useRef(null);

  // Mount-reset
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setPuzzleActive(false);
    setDigits([9, 9, 9, 9, 9, 9, 9, 9]);
    setFeedback(null);
    setAttempts(0);
    setSolved(false);
    setShowFinalLeon(false);
    setSubmitting(false);
    setShaking(false);
    setShowFlash(false);
    setSuccessPhase(0);
    setShowProceed(false);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setPuzzleActive(true);
  }, []);

  const adjustDigit = useCallback((index, direction) => {
    if (!puzzleActive || solved || submitting) return;
    setDigits(prev => {
      const next = [...prev];
      next[index] = (next[index] + direction + 10) % 10;
      return next;
    });
    setFeedback(null);
  }, [puzzleActive, solved, submitting]);

  const handleSubmit = useCallback(() => {
    if (!puzzleActive || solved || submitting) return;
    setSubmitting(true);
    setAttempts(prev => prev + 1);
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 150);

    setTimeout(() => {
      const fb = digits.map((d, i) => {
        if (d === CORRECT_CODE[i]) return 'correct';
        return d < CORRECT_CODE[i] ? 'higher' : 'lower';
      });
      setFeedback(fb);
      setSubmitting(false);

      if (fb.every(f => f === 'correct')) {
        setSolved(true);
        setPuzzleActive(false);
        // Multi-phase success
        setSuccessPhase(1);
        setTimeout(() => setSuccessPhase(2), 500);
        setTimeout(() => setSuccessPhase(3), 1200);
        setTimeout(() => setSuccessPhase(4), 1800);
        setTimeout(() => setShowFinalLeon(true), 2200);
      } else {
        // Shake on wrong
        setShaking(true);
        setTimeout(() => setShaking(false), 400);
      }
    }, 600);
  }, [puzzleActive, solved, submitting, digits]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.VAULT_KEY);
    setShowProceed(true);
  }, [onItemCollected]);

  const correctCount = feedback ? feedback.filter(f => f === 'correct').length : 0;
  const nearMiss = feedback && !solved && correctCount >= 6;

  return (
    <div className="puzzle-container relative overflow-hidden" data-testid="puzzle-safe-cracker">
      <VaultBackground />
      <VaultDoor solved={solved} />

      {/* Submit flash */}
      <AnimatePresence>
        {showFlash && (
          <motion.div initial={{ opacity: 0.4 }} animate={{ opacity: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-white/5 pointer-events-none z-50" />
        )}
      </AnimatePresence>

      {/* Success shockwave */}
      <AnimatePresence>
        {successPhase >= 1 && (
          <motion.div initial={{ opacity: 0.6 }} animate={{ opacity: 0 }} transition={{ duration: 1 }}
            className="absolute inset-0 bg-green-500/10 pointer-events-none z-40" />
        )}
        {successPhase >= 1 && (
          <motion.div
            initial={{ width: 0, height: 0, opacity: 0.5 }}
            animate={{ width: 600, height: 600, opacity: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-green-400/40 pointer-events-none z-40"
          />
        )}
      </AnimatePresence>

      {/* HUD Top Bar */}
      {(puzzleActive || solved) && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="relative z-10 flex items-center justify-between mb-4 px-1">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-terminal tracking-[3px] text-green-600 uppercase">{PUZZLE_2_SAFE_CRACKER.checkpoint}</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield size={10} className="text-green-700" />
            <span className="text-xs font-terminal tracking-wider text-green-700 uppercase">{PUZZLE_2_SAFE_CRACKER.hud.securityLevel}</span>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <div className="text-center mb-5 relative z-10">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-10 h-px bg-gradient-to-r from-transparent to-green-700/40" />
          <Lock size={12} className="text-green-600" />
          <p className="text-xs font-terminal tracking-[6px] text-green-600 uppercase text-glow-green">{PUZZLE_2_SAFE_CRACKER.hud.vaultSystem}</p>
          <Lock size={12} className="text-green-600" />
          <div className="w-10 h-px bg-gradient-to-l from-transparent to-green-700/40" />
        </div>
        <h2 className="font-orbitron text-2xl tracking-wider text-gray-200 uppercase">{PUZZLE_2_SAFE_CRACKER.title}</h2>
        <p className="text-sm text-green-700 font-terminal mt-1">{PUZZLE_2_SAFE_CRACKER.subtitle}</p>
      </div>

      {/* Tutorial screen */}
      <AnimatePresence>
        {showTutorial && <VaultTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {(puzzleActive || solved) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5 relative z-10">
          {/* Vault panel */}
          <div className="max-w-xl mx-auto">
            <div ref={panelRef}
              className={`hud-bracket bg-black/50 p-6 relative overflow-hidden transition-all duration-300 ${shaking ? 'animate-shake' : ''}`}
              style={{ boxShadow: '0 0 20px rgba(0,255,65,0.05), inset 0 0 30px rgba(0,0,0,0.5)', border: '1px solid rgba(0,255,65,0.15)' }}
            >
              <HUDCorners />

              {/* Animated corner cogs */}
              <div className="absolute top-3 left-3 text-green-900/20 animate-spin" style={{ animationDuration: '20s' }}><Cog size={16} /></div>
              <div className="absolute top-3 right-3 text-green-900/20 animate-spin" style={{ animationDuration: '25s', animationDirection: 'reverse' }}><Cog size={16} /></div>
              <div className="absolute bottom-3 left-3 text-green-900/20 animate-spin" style={{ animationDuration: '22s', animationDirection: 'reverse' }}><Cog size={16} /></div>
              <div className="absolute bottom-3 right-3 text-green-900/20 animate-spin" style={{ animationDuration: '18s' }}><Cog size={16} /></div>

              {/* Panel header */}
              <div className="flex items-center justify-center gap-2 mb-4 pb-3 border-b border-green-900/20">
                <div className="w-1 h-1 rounded-full bg-green-700/30" />
                <span className="text-xs font-terminal tracking-[4px] text-green-800/50 uppercase">{PUZZLE_2_SAFE_CRACKER.hud.terminalHeader}</span>
                <div className="w-1 h-1 rounded-full bg-green-700/30" />
              </div>

              {/* Status bar */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Shield size={12} className="text-green-700" />
                  <span className="text-xs font-terminal tracking-[3px] text-green-700 uppercase">{PUZZLE_2_SAFE_CRACKER.hud.vaultLockSystem}</span>
                </div>
                <div className="flex items-center gap-3">
                  {/* Attempt pips */}
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-terminal text-gray-600 mr-1">{PUZZLE_2_SAFE_CRACKER.hud.attLabel}</span>
                    {Array.from({ length: Math.min(attempts, 10) }).map((_, i) => (
                      <div key={i} className="w-1.5 h-1.5 rounded-full bg-yellow-500/60" />
                    ))}
                    {attempts > 0 && <span className="text-xs font-terminal text-yellow-500 ml-1">{attempts}</span>}
                  </div>
                  {/* Correct progress */}
                  {feedback && (
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className={`w-2 h-1 rounded-sm transition-all duration-300 ${feedback[i] === 'correct' ? 'bg-green-500' : 'bg-green-900/30'}`}
                          style={feedback[i] === 'correct' ? { boxShadow: '0 0 4px rgba(34,197,94,0.4)' } : {}} />
                      ))}
                      <span className="text-xs font-terminal text-green-600 ml-1">{correctCount}/8</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Digit format display */}
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-0.5 bg-black/60 rounded px-3 py-1.5 border border-green-900/20">
                  <span className="text-xs font-terminal text-green-800 tracking-wider mr-2">{PUZZLE_2_SAFE_CRACKER.hud.codeLabel}</span>
                  {digits.map((d, i) => (
                    <span key={i} className={`font-orbitron text-sm w-4 text-center transition-colors duration-300 ${
                      feedback?.[i] === 'correct' ? 'text-green-400' : 'text-green-500/80'
                    }`}>
                      {d}
                    </span>
                  ))}
                </div>
              </div>

              {/* Dial wheels — flat row */}
              <div className="flex justify-center gap-2 md:gap-2.5" data-testid="safe-dials">
                {digits.map((d, i) => (
                  <DigitWheel key={i} digit={d} index={i} feedback={feedback} solved={solved}
                    onUp={(idx) => adjustDigit(idx, 1)} onDown={(idx) => adjustDigit(idx, -1)} />
                ))}
              </div>

              {/* Hint legend */}
              {feedback && !solved && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center gap-5 mt-3">
                  {[
                    { icon: ArrowUp, label: PUZZLE_2_SAFE_CRACKER.feedback.goHigher, color: 'text-red-400' },
                    { icon: ArrowDown, label: PUZZLE_2_SAFE_CRACKER.feedback.goLower, color: 'text-red-400' },
                    { icon: Check, label: PUZZLE_2_SAFE_CRACKER.feedback.correct, color: 'text-green-400' },
                  ].map(h => (
                    <div key={h.label} className="flex items-center gap-1">
                      <h.icon size={10} className={h.color} />
                      <span className={`text-xs font-terminal tracking-wider ${h.color}`}>{h.label.toUpperCase()}</span>
                    </div>
                  ))}
                </motion.div>
              )}

              {/* Near-miss text */}
              {nearMiss && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="text-center mt-3">
                  <span className="text-xs font-terminal tracking-[4px] text-green-400 uppercase animate-pulse">
                    {PUZZLE_2_SAFE_CRACKER.hints.almostThere}
                  </span>
                </motion.div>
              )}
            </div>
          </div>

          {/* Submit button */}
          {!solved && (
            <div className="text-center space-y-3">
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 0 25px rgba(234,179,8,0.2)' }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSubmit}
                disabled={submitting}
                className="group relative px-10 py-3 bg-gradient-to-r from-yellow-950/40 via-yellow-900/30 to-yellow-950/40 border-2 border-yellow-600/60 text-yellow-400 font-terminal text-base tracking-[5px] uppercase transition-all hover:border-yellow-500/80 overflow-hidden"
                data-testid="submit-code-btn"
              >
                {/* Shimmer */}
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-500/8 to-transparent pointer-events-none"
                  animate={{ x: ['-200%', '200%'] }} transition={{ duration: 3, repeat: Infinity, repeatDelay: 1 }} />
                {/* Checking progress bar */}
                {submitting && (
                  <motion.div className="absolute bottom-0 left-0 h-0.5 bg-yellow-500/60"
                    initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: 0.55, ease: 'linear' }} />
                )}
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {submitting ? (
                    <><Cog size={14} className="animate-spin" /> {PUZZLE_2_SAFE_CRACKER.submitting}</>
                  ) : (
                    <><Lock size={14} /> {PUZZLE_2_SAFE_CRACKER.submitButton}</>
                  )}
                </span>
              </motion.button>

              {/* Hint area */}
              <div className="flex items-center justify-center gap-2">
                <Calendar size={10} className="text-gray-600 animate-pulse" style={{ animationDuration: '3s' }} />
                <p className="text-xs text-gray-600 font-special italic">
                  {attempts >= 3 ? PUZZLE_2_SAFE_CRACKER.hints.afterThree : PUZZLE_2_SAFE_CRACKER.hints.initial}
                </p>
              </div>
            </div>
          )}

          {/* Solved — multi-phase success */}
          {solved && (
            <div className="text-center space-y-4">
              {/* Phase 2: Vault open icon */}
              <AnimatePresence>
                {successPhase >= 2 && (
                  <motion.div initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="relative w-20 h-20 mx-auto">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                      className="absolute inset-0 border-2 border-dashed border-green-500/20 rounded-full" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center"
                        style={{ boxShadow: '0 0 30px rgba(0,255,65,0.25)' }}>
                        <KeyRound size={28} className="text-green-400" />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Phase 3: Text */}
              <AnimatePresence>
                {successPhase >= 3 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <motion.p initial={{ letterSpacing: '2px' }} animate={{ letterSpacing: '6px' }}
                      transition={{ duration: 0.8 }}
                      className="font-orbitron text-green-400 text-sm">
                      {PUZZLE_2_SAFE_CRACKER.completion.heading}
                    </motion.p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Phase 4: Details */}
              <AnimatePresence>
                {successPhase >= 4 && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="space-y-2">
                    <div className="inline-flex items-center gap-1 bg-green-950/30 border border-green-800/30 rounded px-3 py-1">
                      <span className="text-xs font-terminal text-green-600 tracking-wider">CODE: {CORRECT_CODE.join('')}</span>
                    </div>
                    <p className="text-gray-400 font-rajdhani">
                      <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                        className="text-yellow-500/80" style={{ textShadow: '0 0 8px rgba(234,179,8,0.3)' }}>
                        {PUZZLE_2_SAFE_CRACKER.completion.itemText}
                      </motion.span>
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {showProceed && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              className="text-center mt-8 relative z-20"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onComplete?.()}
                className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
              >
                <span className="relative z-10">{PUZZLE_2_SAFE_CRACKER.proceedButton}</span>
                <motion.div
                  className="absolute inset-0"
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
                />
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      )}

      {showLeon && (
        <LeonDialogue
          message={PUZZLE_2_SAFE_CRACKER.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={PUZZLE_2_SAFE_CRACKER.leon.complete}
          onDismiss={handleFinalDismiss}
        />
      )}

      {/* Shake keyframes (CSS) */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-4px); }
          30% { transform: translateX(4px); }
          45% { transform: translateX(-3px); }
          60% { transform: translateX(3px); }
          75% { transform: translateX(-1px); }
          90% { transform: translateX(1px); }
        }
        .animate-shake {
          animation: shake 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}
